// src/pages/CompanyGroRuleActionLinksPage.tsx
import { useMemo, useState } from "react";
import { Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCompanyGroTemplates,
  useCreateCompanyGroRuleActionLink,
  useDeleteCompanyGroRuleActionLink,
} from "@/hooks/useCompanyGroTemplates";
import { PERMISSIONS } from "@/lib/permissions";

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function CompanyGroRuleActionLinksPage() {
  const dataQ = useCompanyGroTemplates();
  const createLink = useCreateCompanyGroRuleActionLink();
  const deleteLink = useDeleteCompanyGroRuleActionLink();
  const [ruleId, setRuleId] = useState("");
  const [planId, setPlanId] = useState("");

  const activeRules = useMemo(
    () => dataQ.data?.dimension_risk_rules.filter((item) => item.is_active) ?? [],
    [dataQ.data?.dimension_risk_rules],
  );
  const activePlans = useMemo(
    () => dataQ.data?.action_plan_models.filter((item) => item.is_active) ?? [],
    [dataQ.data?.action_plan_models],
  );
  const links = dataQ.data?.dimension_risk_rule_action_plan_models ?? [];

  async function handleCreate() {
    if (!ruleId || !planId) {
      toast.error("Selecione a regra e o Plano de Ação.");
      return;
    }
    try {
      await createLink.mutateAsync({ ruleId, planId });
      setRuleId("");
      setPlanId("");
      toast.success("Plano recomendado vinculado à regra.");
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao criar vínculo."));
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteLink.mutateAsync(id);
      toast.success("Vínculo removido.");
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao remover vínculo."));
    }
  }

  return (
    <div>
      <PageHeader
        title="Planos recomendados por regra"
        description="Vincule Planos de Ação modelo às regras Dimensão x Risco da Empresa."
      />

      <Card className="mb-4">
        <CardContent className="grid gap-3 p-4 md:grid-cols-[1fr_1fr_auto]">
          <Select value={ruleId} onValueChange={setRuleId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a regra" />
            </SelectTrigger>
            <SelectContent>
              {activeRules.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.dimension_name} → {item.risk_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o Plano de Ação" />
            </SelectTrigger>
            <SelectContent>
              {activePlans.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.code} - {item.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <PermissionButton
            permission={PERMISSIONS.TENANT_MODELS_GRO_WRITE}
            onClick={handleCreate}
            disabled={createLink.isPending || !ruleId || !planId}
          >
            <Link2 className="mr-2 h-4 w-4" />
            Vincular
          </PermissionButton>
        </CardContent>
      </Card>

      {dataQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando vínculos...</Card>
      ) : links.length === 0 ? (
        <EmptyState
          title="Nenhum Plano de Ação recomendado"
          description="Selecione uma regra e um Plano de Ação para criar o primeiro vínculo."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Regra</TableHead>
                <TableHead>Plano recomendado</TableHead>
                <TableHead>Padrão</TableHead>
                <TableHead className="w-[100px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.dimension_name} → {item.risk_name}
                  </TableCell>
                  <TableCell>{item.action_plan_title}</TableCell>
                  <TableCell>{item.is_default ? "Sim" : "Não"}</TableCell>
                  <TableCell className="text-right">
                    <PermissionIconButton
                      label="Remover vínculo"
                      permission={PERMISSIONS.TENANT_MODELS_GRO_WRITE}
                      destructive
                      disabled={deleteLink.isPending}
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </PermissionIconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
